import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import {
  Award,
  CheckCircle2,
  FileText,
  ImageOff,
  Loader2,
  Trash,
  UploadCloud,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import ConfirmDialog from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { ProfileInfoCard } from "@/components/profile-info-card";
import { SiteManagerGuard } from "@/components/role-guard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Heading } from "@/components/ui/typography";
import {
  calculateCertificationExpiryStatus,
  certificationStatusToBadgeVariant,
  formatCertificationExpiryStatus,
} from "@/lib/certifications";
import { cn } from "@/lib/utils";
import { trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type WorkerCertificate =
  inferRouterOutputs<AppRouter>["getWorkerCertificatesByManager"][number];
type UploadResponse =
  inferRouterOutputs<AppRouter>["uploadWorkerCertificateByManager"];
type UploadedCertificate = UploadResponse["certificate"];

const UPLOAD_STEPS = [
  "Uploading file\u2026",
  "Reading text\u2026",
  "Extracting fields\u2026",
] as const;

export default function WorkerCertifications() {
  const { id } = useParams();
  const workerId = id ?? "";
  const queryClient = useQueryClient();

  // List state
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Upload sheet state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [uploadedCert, setUploadedCert] = useState<UploadedCertificate | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [uploadStep, setUploadStep] = useState(0);

  const profile = useQuery(
    trpc.getWorkerProfile.queryOptions({ id: workerId })
  );

  const certificatesQuery = useQuery(
    trpc.getWorkerCertificatesByManager.queryOptions({ workerId })
  );

  const deleteMutation = useMutation({
    ...trpc.deleteWorkerCertificateByManager.mutationOptions(),
    onSuccess: () => {
      toast.success("Certification deleted");
      queryClient.invalidateQueries({
        queryKey: trpc.getWorkerCertificatesByManager.queryKey({ workerId }),
      });
      setDeleteId(null);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const uploadMutation = useMutation({
    ...trpc.uploadWorkerCertificateByManager.mutationOptions(),
    onSuccess: (data) => {
      setUploadedCert(data.certificate);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (!uploadMutation.isPending) {
      setUploadStep(0);
      return;
    }
    const interval = setInterval(() => {
      setUploadStep((prev) => Math.min(prev + 1, UPLOAD_STEPS.length - 1));
    }, 1800);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadMutation.isPending]);

  const discardMutation = useMutation({
    ...trpc.deleteWorkerCertificateByManager.mutationOptions(),
    onSuccess: () => {
      toast.success("Upload discarded");
      closeSheet();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const closeSheet = () => {
    setSheetOpen(false);
    setUploadedCert(null);
    setIsDragging(false);
    setSelectedFileName(null);
    setUploadStep(0);
  };

  const handleSheetOpenChange = (open: boolean) => {
    if (!open && uploadedCert) {
      discardMutation.mutate({ id: uploadedCert.id });
    } else if (!open) {
      closeSheet();
    } else {
      setSheetOpen(true);
    }
  };

  const handleFile = (file: File) => {
    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      uploadMutation.mutate({
        workerId,
        fileBase64: base64,
        fileMimeType: file.type || "application/octet-stream",
      });
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleConfirm = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.getWorkerCertificatesByManager.queryKey({ workerId }),
    });
    toast.success("Certification saved");
    closeSheet();
  };

  const certificates = certificatesQuery.data ?? [];

  return (
    <SiteManagerGuard>
      <div className="space-y-6">
        <PageHeader
          title={profile.data?.fullName ?? "Worker Certifications"}
          description="Certifications"
          backTo={`/workers/${workerId}`}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-8 items-start">
          <div className="lg:sticky lg:top-24">
            {profile.data && <ProfileInfoCard profile={profile.data} />}
          </div>
          <div>
            <div className="flex items-center justify-between mb-4">
              <Heading variant="subsection" as="h2">
                Certifications
              </Heading>
              <Button onClick={() => setSheetOpen(true)}>
                Upload certification
              </Button>
            </div>

            {certificates.length === 0 ? (
              <div className="flex flex-col items-center text-center p-12 space-y-2">
                <Award className="w-16 h-16 mb-2 text-muted-foreground" />
                <p className="font-semibold">No certifications found</p>
                <p className="text-sm text-muted-foreground">
                  Upload a certificate to get started.
                </p>
              </div>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                {certificates.map((cert: WorkerCertificate) => {
                  const expiryDate = toDate(cert.expiryDate);
                  const expiryStatus =
                    calculateCertificationExpiryStatus(expiryDate);
                  const downloadUrl =
                    "documentUrl" in cert ? cert.documentUrl : null;
                  const validation = cert.validation as
                    | {
                        expiryValid: boolean | null;
                        certificateNumberPatternValid: boolean | null;
                        issues: string[];
                      }
                    | null
                    | undefined;
                  const userIssues = (validation?.issues ?? []).filter(
                    (issue) => !issue.startsWith("Parser failure")
                  );
                  const meaningfulValidations = validation
                    ? [
                        { label: "Expiry", value: validation.expiryValid },
                        {
                          label: "Number",
                          value: validation.certificateNumberPatternValid,
                        },
                      ].filter((v) => v.value !== null)
                    : [];

                  return (
                    <Card key={cert.id}>
                      <CardHeader>
                        <div>
                          <CardTitle>{cert.type}</CardTitle>
                          <CardDescription>{cert.reference}</CardDescription>
                        </div>
                        <CardAction className="flex items-center gap-2">
                          <Badge
                            variant={certificationStatusToBadgeVariant(
                              expiryStatus
                            )}
                            className="shrink-0"
                          >
                            {formatCertificationExpiryStatus(expiryStatus)}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="shrink-0 uppercase"
                          >
                            {cert.status}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(cert.id)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </CardAction>
                      </CardHeader>
                      <CardContent className="space-y-4 text-sm">
                        <div className="overflow-hidden rounded-md border bg-muted/20 flex items-center justify-center min-h-40">
                          {downloadUrl ? (
                            <>
                              <img
                                src={downloadUrl}
                                alt={`${cert.type ?? "Certificate"} document`}
                                className="w-full object-contain max-h-64"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  (
                                    e.currentTarget
                                      .nextElementSibling as HTMLElement
                                  ).style.display = "flex";
                                }}
                              />
                              <div className="hidden flex-col items-center gap-2 text-muted-foreground p-8">
                                <FileText className="h-8 w-8" />
                                <p className="text-xs">PDF — no preview</p>
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-col items-center gap-2 text-muted-foreground p-8">
                              <ImageOff className="h-8 w-8" />
                              <p className="text-xs">No preview available</p>
                            </div>
                          )}
                        </div>
                        <Separator />
                        <DetailList
                          items={[
                            {
                              label: "Issuing body",
                              value: normalise(cert.issuingBody),
                            },
                            {
                              label: "Issue date",
                              value: formatOptionalDate(cert.issueDate),
                            },
                            {
                              label: "Expiry date",
                              value: formatOptionalDate(cert.expiryDate),
                            },
                            {
                              label: "Full name",
                              value: normalise(cert.fullName),
                            },
                            {
                              label: "Qualification",
                              value: normalise(cert.qualification),
                            },
                            {
                              label: "Card type",
                              value: normalise(cert.cardType),
                            },
                            {
                              label: "Employer",
                              value: normalise(cert.employerName),
                            },
                          ]}
                        />
                        {meaningfulValidations.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {meaningfulValidations.map((v) => (
                              <Badge
                                key={v.label}
                                variant={badgeVariant(v.value)}
                              >
                                {v.label}: {formatValidation(v.value)}
                              </Badge>
                            ))}
                          </div>
                        ) : null}
                        {userIssues.length > 0 ? (
                          <Alert variant="warning">
                            <AlertTitle>Check details</AlertTitle>
                            <AlertDescription>
                              <ul className="list-disc space-y-1 pl-4 text-left">
                                {userIssues.map((issue) => (
                                  <li key={issue}>{issue}</li>
                                ))}
                              </ul>
                            </AlertDescription>
                          </Alert>
                        ) : null}
                        {downloadUrl && (
                          <Button
                            asChild
                            variant="link"
                            size="sm"
                            className="px-0"
                          >
                            <a
                              href={downloadUrl}
                              download={`${cert.type}-${cert.reference}`}
                            >
                              Download certificate
                            </a>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </ul>
            )}

            {/* Upload sheet */}
            <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Upload Certification</SheetTitle>
                  <SheetDescription>
                    Upload a certification document for this worker
                  </SheetDescription>
                </SheetHeader>
                <div className="px-6 pb-6 space-y-4">
                  {!uploadedCert ? (
                    <>
                      <input
                        id="upload-file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg"
                        className="sr-only"
                        onChange={handleFileChange}
                        disabled={uploadMutation.isPending}
                      />
                      {uploadMutation.isPending ? (
                        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/20 p-12 text-center">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">
                              {UPLOAD_STEPS[uploadStep]}
                            </p>
                            {selectedFileName && (
                              <p className="text-xs text-muted-foreground truncate max-w-[220px]">
                                {selectedFileName}
                              </p>
                            )}
                            <div className="flex justify-center gap-1 pt-1">
                              {UPLOAD_STEPS.map((step, i) => (
                                <span
                                  key={step}
                                  className={cn(
                                    "inline-block h-1 w-6 rounded-full transition-colors duration-500",
                                    i <= uploadStep
                                      ? "bg-primary"
                                      : "bg-muted-foreground/20"
                                  )}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : uploadMutation.isError ? (
                        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-destructive/40 bg-destructive/5 p-12 text-center">
                          <div className="rounded-full bg-destructive/10 p-3">
                            <UploadCloud className="h-7 w-7 text-destructive" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm font-medium">Upload failed</p>
                            <p className="text-xs text-muted-foreground">
                              {uploadMutation.error?.message ??
                                "Something went wrong."}
                            </p>
                          </div>
                          <label
                            htmlFor="upload-file"
                            className="cursor-pointer"
                          >
                            <button
                              type="button"
                              className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                              onClick={() => uploadMutation.reset()}
                            >
                              Try again
                            </button>
                          </label>
                        </div>
                      ) : (
                        <label
                          htmlFor="upload-file"
                          className={cn(
                            "flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-12 text-center transition-colors cursor-pointer",
                            isDragging
                              ? "border-primary bg-primary/5"
                              : "border-muted-foreground/30 bg-muted/20 hover:border-muted-foreground/50 hover:bg-muted/30"
                          )}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={handleDrop}
                        >
                          <UploadCloud className="h-10 w-10 text-muted-foreground" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-muted-foreground">
                              PDF, JPG or JPEG
                            </p>
                          </div>
                        </label>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Review summary banner */}
                      {uploadedCert.processingError ? (
                        <div className="flex items-start gap-3 rounded-lg border border-warning/25 bg-warning/12 px-4 py-3 text-sm text-warning-foreground">
                          <span className="mt-0.5 text-warning">⚠</span>
                          <div>
                            <p className="font-medium">
                              Some fields could not be read
                            </p>
                            <p className="opacity-90">
                              Review the detected text and confirm the details
                              are correct before saving.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 rounded-lg border border-success/25 bg-success/12 px-4 py-3 text-sm text-success-foreground">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                          <p className="font-medium">
                            Certificate read successfully
                          </p>
                        </div>
                      )}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle>
                            {uploadedCert.type ?? "Certificate"}
                          </CardTitle>
                          <CardDescription>
                            {uploadedCert.reference ||
                              "No certificate number detected"}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <UploadDetailGrid certificate={uploadedCert} />
                          <UploadValidationSummary certificate={uploadedCert} />
                          {uploadedCert.processingError ? (
                            <Alert variant="warning">
                              <AlertTitle>Details may be incomplete</AlertTitle>
                              <AlertDescription>
                                Some fields could not be read automatically.
                                Review the detected text and verify the
                                information is correct before saving.
                              </AlertDescription>
                            </Alert>
                          ) : null}
                        </CardContent>
                      </Card>
                      {uploadedCert.ocrText ? (
                        <div className="rounded-md border bg-muted/40 p-3 text-sm">
                          <p className="font-medium mb-1">Detected text</p>
                          <p className="text-muted-foreground whitespace-pre-wrap max-h-48 overflow-y-auto">
                            {uploadedCert.ocrText}
                          </p>
                        </div>
                      ) : null}
                      <div className="flex gap-2">
                        <Button
                          onClick={handleConfirm}
                          disabled={discardMutation.isPending}
                        >
                          Save certification
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() =>
                            discardMutation.mutate({ id: uploadedCert.id })
                          }
                          disabled={discardMutation.isPending}
                        >
                          {discardMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Discarding…
                            </>
                          ) : (
                            "Discard"
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
        title="Delete certification?"
        description="This will permanently remove the certification."
        confirmLabel="Yes, delete"
        pendingLabel="Deleting..."
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteId) {
            deleteMutation.mutate({ id: deleteId });
          }
        }}
      />
    </SiteManagerGuard>
  );
}

function UploadDetailGrid({
  certificate,
}: {
  certificate: UploadedCertificate;
}) {
  const items = [
    { label: "Full name", value: certificate.fullName },
    { label: "Qualification", value: certificate.qualification },
    { label: "Card type", value: certificate.cardType },
    { label: "Employer", value: certificate.employerName },
    { label: "Issue date", value: formatOptionalDate(certificate.issueDate) },
    { label: "Expiry date", value: formatOptionalDate(certificate.expiryDate) },
    { label: "Issuing body", value: certificate.issuingBody },
  ].filter((item) => item.value);

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No additional details detected.
      </p>
    );
  }

  return (
    <dl className="grid gap-3 grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="space-y-0.5">
          <dt className="text-xs uppercase text-muted-foreground tracking-wide">
            {item.label}
          </dt>
          <dd className="text-sm font-medium">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function UploadValidationSummary({
  certificate,
}: {
  certificate: UploadedCertificate;
}) {
  const validation = certificate.validation as
    | {
        expiryValid?: boolean | null;
        certificateNumberPatternValid?: boolean | null;
        issues?: string[] | null;
      }
    | null
    | undefined;

  if (!validation) return null;

  const states: Array<{ label: string; value: boolean | null }> = [
    { label: "Expiry", value: validation.expiryValid ?? null },
    {
      label: "Number format",
      value: validation.certificateNumberPatternValid ?? null,
    },
  ];

  // Only show badges that have a definite result; suppress all-unknown noise
  const meaningfulStates = states.filter((s) => s.value !== null);

  // Strip internal parser failure messages — they're not actionable for users
  const issues = (validation.issues ?? []).filter(
    (issue) => !issue.startsWith("Parser failure")
  );

  if (meaningfulStates.length === 0 && issues.length === 0) return null;

  return (
    <div className="space-y-2">
      {meaningfulStates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {meaningfulStates.map((state) => (
            <Badge key={state.label} variant={badgeVariant(state.value)}>
              {state.label}: {formatValidation(state.value)}
            </Badge>
          ))}
        </div>
      )}
      {issues.length > 0 ? (
        <Alert variant="warning">
          <AlertTitle>Validation notices</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 space-y-1 text-left">
              {issues.map((issue: string) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

function DetailList({
  items,
}: {
  items: Array<{ label: string; value?: string | null }>;
}) {
  const filtered = items.filter((item) => item.value);
  if (filtered.length === 0) {
    return null;
  }

  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {filtered.map((item) => (
        <div key={item.label} className="space-y-0.5">
          <dt className="text-xs uppercase text-muted-foreground tracking-wide">
            {item.label}
          </dt>
          <dd className="text-sm font-medium">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function normalise(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.toLowerCase() === "unknown") return null;
  return trimmed || null;
}

function formatOptionalDate(value: string | Date | null | undefined) {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatValidation(value: boolean | null) {
  if (value === true) return "pass";
  if (value === false) return "review";
  return "unknown";
}

function badgeVariant(
  value: boolean | null
): "default" | "outline" | "destructive" {
  if (value === true) return "default";
  if (value === false) return "destructive";
  return "outline";
}
